/** The request permission vocabulary — `view`, `create` and the granular record permissions. */
export const REQUEST_PERMISSIONS = [
  'view',
  'create',
  'edit_metadata',
  'manage_participants',
  'pause',
  'reject',
  'delete',
  'edit_submissions',
  'view_documents',
  'edit_documents',
  'manage_documents',
  'manage_assignees',
  'skip',
  'rollback',
  'manage_integrations',
  'manage_sourcing',
  'administer',
  'view_tprm',
  'manage_tprm',
] as const
export type RequestPermission = (typeof REQUEST_PERMISSIONS)[number]

/**
 * The permissions with no record behind them. The organisation answers each (`can_create_request`),
 * so they have no per-request relation, scope arm or confidential twin — only the toggle on the set
 * and the `can_*` on the grant.
 */
export const REQUEST_ORGANISATION_PERMISSIONS = ['create'] as const satisfies readonly RequestPermission[]
export type RequestOrganisationPermission = (typeof REQUEST_ORGANISATION_PERMISSIONS)[number]

export type RequestRecordPermission = Exclude<RequestPermission, RequestOrganisationPermission>

const organisationPermissions: readonly string[] = REQUEST_ORGANISATION_PERMISSIONS

const isRecordPermission = (permission: RequestPermission): permission is RequestRecordPermission =>
  !organisationPermissions.includes(permission)

/** The permissions fanned out per request: every relation the record, its scope and its twins carry. */
export const REQUEST_RECORD_VERBS: readonly RequestRecordPermission[] = REQUEST_PERMISSIONS.filter(isRecordPermission)

export const requestGrantsRelation = (permission: RequestPermission): `grants_${RequestPermission}` => `grants_${permission}`
export const requestGrantedRelation = (permission: RequestPermission): `granted_${RequestPermission}` => `granted_${permission}`
export const requestCanRelation = (permission: RequestRecordPermission): `can_${RequestRecordPermission}` => `can_${permission}`
